<template>
  <div class="confetti-sprite" aria-hidden="true">
    <div
      v-for="(p, i) in pieces"
      :key="i"
      class="confetti-sprite__piece"
      :style="{
        '--start-x': p.startX,
        '--start-y': p.startY,
        '--burst-x': p.burstX,
        '--burst-y': p.burstY,
        '--drift-x': p.driftX,
        '--fall-y': p.fallY,
        '--rotation': `${p.rotation}deg`,
        '--delay': `${p.delay}s`,
        '--color': p.color,
        '--w': `${p.w}px`,
        '--h': `${p.h}px`,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useResponsive } from '../../helpers/vueHelpers'

const COLORS = ['#87AFB7', '#A2ACBD', '#EE96AD', '#FEF9E7', '#E3F2FD', '#FBE6EB', '#F9E79D', '#EAF1F3', '#AAC6CC']

interface Piece {
  startX: string
  startY: string
  burstX: string
  burstY: string
  driftX: string
  fallY: string
  rotation: number
  delay: number
  color: string
  w: number
  h: number
}

const pieces = ref<Piece[]>([])
const { mobile } = useResponsive()

const COUNT = mobile.value ? 36 : 64

function rnd(min: number, max: number) {
  return min + Math.random() * (max - min)
}

onMounted(() => {
  pieces.value = Array.from({ length: COUNT }, (_, i) => {
    // Small area: center around 50%, 80% with ±4% jitter
    const startX = 50 + rnd(-1, 1)
    const startY = 95 + rnd(-6, 6)

    // Spread across in X
    const burstX = rnd(-42, 42)

    // Peak height: some reach the top (-80vh), others stop at -25vh, -50vh, etc.
    const burstY = rnd(-80, -22)

    return {
      startX: `${startX.toFixed(2)}%`,
      startY: `${startY.toFixed(2)}%`,
      burstX: `${burstX.toFixed(2)}vw`,
      burstY: `${burstY.toFixed(2)}vh`,
      driftX: `${rnd(-14, 14).toFixed(2)}vw`,
      fallY: `${rnd(105, 145).toFixed(2)}vh`,
      rotation: rnd(180, 720) * (Math.random() > 0.5 ? 1 : -1),
      delay: rnd(0, 0.3),
      color: COLORS[i % COLORS.length],
      w: mobile.value ? rnd(6, 14) : rnd(10, 30),
      h: mobile.value ? rnd(4, 6) : Math.random() > 0.5 ? rnd(4, 8) : rnd(8, 14),
    }
  })
})
</script>

<style lang="scss" scoped>
.confetti-sprite {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.confetti-sprite__piece {
  position: absolute;
  left: var(--start-x);
  top: var(--start-y);
  width: var(--w);
  height: var(--h);
  background-color: var(--color);
  border-radius: 1px;
  animation: confetti-burst-fall 5s cubic-bezier(0.45, 0, 0.55, 1) var(--delay) infinite;
  will-change: transform;
}

@keyframes confetti-burst-fall {
  0% {
    transform: translate(-50%, -50%) translate(0, 0) rotate(0deg);
    opacity: 1;
  }
  20% {
    transform: translate(-50%, -50%) translate(var(--burst-x), var(--burst-y)) rotate(calc(var(--rotation) * 0.36));
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%)
      translate(calc(var(--burst-x) + var(--drift-x)), calc(var(--burst-y) + var(--fall-y))) rotate(var(--rotation));
    opacity: 0.85;
  }
}
</style>
